import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft } from 'lucide-react';
import { RouteTransitionLogo } from '../components/RouteTransitionLogo';
import { getNotificationActionPath } from '../utils/notificationRouting';
import { getListingPresentation } from '../utils/hostListings';
import { useBackTarget } from '../hooks/useBackTarget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const font = "'Inria Serif', serif";

const formatGroupDate = (value) => new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(value));
const formatDisplayDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: '2-digit' }).format(date);
};
// Both `detected` (legacy manual-approval flow) and `confirmed_by_host`
// (2026-02+ flow — iCal reservations skip the manual approval step and
// open directly as shareable) are freshly-detected iCal reservations
// that should render as the rich reservation card. Any legacy rows
// stored with raw-ISO body strings are ALSO fixed by this — the rich
// card builds its date line from `metadata.check_in_date`/`checkout_date`
// via `formatDisplayDate`, so the notification body string is never
// rendered verbatim.
const isReservationNotification = (item) => item?.metadata?.source === 'ical' && ['detected', 'confirmed_by_host'].includes(item?.metadata?.status);
const buildReservationDateLine = (item) => {
  const start = item?.metadata?.check_in_date;
  const end = item?.metadata?.checkout_date;
  return start && end ? `${formatDisplayDate(start)} — ${formatDisplayDate(end)}` : '';
};

export default function NotificationsPage() {
  const navigate = useNavigate();
  const fallbackBack = useBackTarget();
  // Prefer real history (bell always pushes /notifications onto the stack)
  // so we return to the caller's current screen. Only fall back to the
  // role-home when the page was opened as a cold-start deep link with no
  // history entries.
  const goBack = () => {
    const hasHistory =
      typeof window !== 'undefined' &&
      window.history &&
      (window.history.state?.idx || 0) > 0;
    if (hasHistory) {
      navigate(-1);
      return;
    }
    fallbackBack();
  };
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/notifications/history`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setNotifications(response.data || []);
      } catch (error) {
        console.error('Failed to load notifications:', error);
      }
    };

    fetchNotifications();
  }, []);

  const groupedNotifications = useMemo(() => notifications.reduce((groups, item) => {
    const key = formatGroupDate(item.created_at);
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(item);
    return groups;
  }, {}), [notifications]);

  return (
    <div className="app-shell" data-testid="notifications-history-page" style={{ minHeight: '100vh', backgroundColor: 'var(--surface)', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: 40 }}>
      <div style={{ width: '100%', maxWidth: 390, display: 'flex', justifyContent: 'flex-start', padding: '16px 20px 0' }}>
        <button onClick={goBack} data-testid="notifications-history-back" style={{ background: 'none', border: 'none', cursor: 'pointer' }}><ChevronLeft size={30} color="var(--text)" strokeWidth={2} /></button>
      </div>
      <RouteTransitionLogo width={100} height={89} dataTestId="notifications-history-shared-logo" style={{ marginTop: 8 }} />
      <h2 style={{ fontFamily: font, fontSize: 26, fontWeight: 700, color: 'var(--text)', marginTop: 8, marginBottom: 24 }}>Notifications</h2>

      <div style={{ width: '100%', maxWidth: 360, padding: '0 16px' }}>
        {notifications.length === 0 ? (
          <div style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--card-border)', borderRadius: 24, boxShadow: 'var(--premium-shadow-main)', padding: '18px 16px' }}>
            <p style={{ fontFamily: font, fontSize: 18, fontWeight: 700, color: 'var(--text-2)', margin: 0, textAlign: 'center' }}>No notifications yet</p>
          </div>
        ) : Object.entries(groupedNotifications).map(([date, items]) => (
          <div key={date} style={{ marginBottom: 20 }}>
            <p style={{ fontFamily: font, fontSize: 14, color: 'var(--text-2)', margin: '0 0 6px' }}>{date}</p>
            {items.map((item) => {
              const actionPath = getNotificationActionPath(item);
              const reservationMeta = getListingPresentation({ title: item?.metadata?.listing_name, address: item?.metadata?.listing_address });
              const reservationImage = item?.metadata?.listing_image || '/allure-logo-mono.png';
              const isDetected = ['detected', 'confirmed_by_host'].includes(item?.metadata?.status);
              return (
              <div
                key={item.id}
                data-testid={`notifications-history-item-${item.id}`}
                onClick={() => actionPath && navigate(actionPath)}
                style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--card-border)', borderRadius: 24, boxShadow: 'var(--premium-shadow-main)', padding: '16px 18px', marginBottom: 8, cursor: actionPath ? 'pointer' : 'default' }}
              >
                {isReservationNotification(item) ? (
                  <div style={{ display: 'grid', gap: 10 }}>
                    <p style={{ fontFamily: font, fontSize: 20, fontWeight: 700, color: isDetected ? 'var(--status-success-text)' : 'var(--text)', margin: 0, lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {isDetected ? 'Reservation detected' : item.title}
                    </p>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <div style={{ width: 62, height: 62, borderRadius: 18, overflow: 'hidden', background: 'var(--surface-2)', flexShrink: 0 }}>
                        <img src={reservationImage} alt={reservationMeta.displayName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                      <div style={{ minWidth: 0, display: 'grid', gap: 4, flex: 1 }}>
                        <p style={{ fontFamily: font, fontSize: 18, fontWeight: 700, color: 'var(--text)', margin: 0, lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {reservationMeta.displayName}
                        </p>
                        <p style={{ fontFamily: font, fontSize: 14, color: 'var(--text-2)', margin: 0, lineHeight: 1.1 }}>
                          {reservationMeta.displayAddress || 'Location unavailable'}
                        </p>
                      </div>
                    </div>
                    <p style={{ fontFamily: font, fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: 0, lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {buildReservationDateLine(item)}
                    </p>
                  </div>
                ) : (
                  <>
                    <p style={{ fontFamily: font, fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: 0, lineHeight: 1.2 }}>{item.title}</p>
                    <p style={{ fontFamily: font, fontSize: 18, fontWeight: 700, color: 'var(--text)', margin: '8px 0 0', lineHeight: 1.35 }}>{item.message}</p>
                  </>
                )}
              </div>
            );})}
          </div>
        ))}
      </div>
    </div>
  );
}